import {
  CloseFullscreen,
  CloudUpload,
  Download,
  OpenInFull,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import * as XLSX from 'xlsx';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import type { TranslationKey } from '../../../types/i18n';
import {
  uploadEmployeeDirectory,
  UploadEmployeeDirectoryResult,
  validateEmployeeDirectory,
} from '../../../services/agentService';
import Button from '../../shared/Button/Button';

const STEPS: TranslationKey[] = [
  'Download Template',
  'Instructions',
  'Preview',
  'Upload',
];

const REQUIRED_COLUMNS = ['First Name', 'Last Name'];
const OPTIONAL_COLUMNS = [
  'Phone Number',
  'Extension', // call routing to a specific employee or department
  'Backup Phone Number',
  'Department Name',
  'Department Phone Number',
  'Department Email',
  'Job Title',
  'Email',
  'Backup Email',
  'Company',
  'Location',
  'Department Description',
];
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

// Mirrors the backend's column-name -> field-name convention
// (libs/employee_directory_upload.COLUMN_TO_FIELD): lowercase, spaces to
// underscores. Used to map validate_employee_directory's row objects back
// onto ALL_COLUMNS for display.
const toFieldKey = (columnLabel: string) =>
  columnLabel.toLowerCase().replace(/ /g, '_');

// Staged files are persisted base64-encoded inside the shared
// elora_agent_form_state sessionStorage blob (AgentForm's
// saveCapabilitiesData). sessionStorage caps out around 5 MB and base64
// inflates by ~33%, so cap raw files at 3 MB to keep the whole blob —
// including every other persisted form field — safely under quota.
const MAX_STAGED_FILE_BYTES = 3 * 1024 * 1024;

interface EmployeeDirectoryUploadModalProps {
  agentId: string;
  open: boolean;
  onClose: () => void;
  /** During agent creation no real agent id exists yet, so the file can't be
   * uploaded live — stage it in form state instead and let the caller upload
   * it once the agent has been created. */
  deferUpload?: boolean;
}

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
  });

const generateTemplate = () => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([ALL_COLUMNS]);
  XLSX.utils.book_append_sheet(wb, ws, 'Employee Directory');
  XLSX.writeFile(wb, 'employee_directory_template.xlsx');
};

const EmployeeDirectoryUploadModal: React.FC<
  EmployeeDirectoryUploadModalProps
> = ({ agentId, open, onClose, deferUpload = false }) => {
  const { setValue } = useFormContext();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<UploadEmployeeDirectoryResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayMessage = useSnackBar();
  const { t } = useTranslation();

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setPreviewRows([]);
    setPreviewHeaders([]);
    setTotalRows(0);
    setFileError(null);
    setIsValidating(false);
    setUploadResult(null);
    setUploadError(null);
    setIsExpanded(false);
    setPage(0);
    setSearchQuery('');
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected) return;

    if (!selected.name.endsWith('.xlsx')) {
      setFileError(t('Only .xlsx files are accepted.'));
      return;
    }

    if (deferUpload && selected.size > MAX_STAGED_FILE_BYTES) {
      setFileError(t('The file is too large. Files up to 3 MB are supported.'));
      return;
    }

    setFileError(null);
    setUploadError(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setFileError(t('The file could not be read. Please try again.'));
    };
    reader.onload = async e => {
      let wb: XLSX.WorkBook;
      let rows: string[][];
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      } catch {
        setFileError(
          t(
            'The file could not be read. Please make sure it is a valid .xlsx file.'
          )
        );
        return;
      }
      if (rows.length < 2) {
        setFileError(t('The file appears to be empty.'));
        return;
      }
      // Normalize headers to lowercase+trimmed to match backend's case-insensitive
      // column matching (backend does col.strip().lower() before lookup). Column
      // and row validation itself (required fields, string length, row cap, row
      // content, ...) is not duplicated here — it's delegated to the backend via
      // validateEmployeeDirectory below, which is the single source of truth.
      const fileHeaders = rows[0] as string[];
      const columnIndexMap = Object.fromEntries(
        fileHeaders.map((h, i) => [String(h).trim().toLowerCase(), i])
      );

      // Inject columns the backend requires but the file omits.
      // The backend rejects uploads missing any expected column header; users
      // may legitimately leave optional columns (e.g. Backup Phone Number) out
      // of their file entirely if they have no data for them.
      const missingCols = ALL_COLUMNS.filter(
        col => columnIndexMap[col.toLowerCase()] === undefined
      );
      let fileToUpload = selected;
      if (missingCols.length > 0) {
        const patchedRows = [
          [...(rows[0] as string[]), ...missingCols],
          ...(rows.slice(1) as string[][]),
        ];
        const patchedWs = XLSX.utils.aoa_to_sheet(patchedRows);
        wb.Sheets[wb.SheetNames[0]] = patchedWs;
        const bytes: Uint8Array = XLSX.write(wb, {
          type: 'array',
          bookType: 'xlsx',
        });
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fileToUpload = new File([blob], selected.name, { type: blob.type });
      }

      // During agent creation there is no real agent id yet (deferUpload), so
      // the backend can't be asked to validate against it — show a raw,
      // unvalidated preview instead. Full validation happens once the agent
      // exists and the staged file is actually uploaded.
      if (deferUpload) {
        const dataRows = (rows.slice(1) as string[][])
          .filter(row =>
            row.some(cell => cell !== undefined && String(cell).trim() !== '')
          )
          .map(row =>
            ALL_COLUMNS.map(col => {
              const idx = columnIndexMap[col.toLowerCase()];
              return idx !== undefined ? String(row[idx] ?? '') : '';
            })
          );
        setPreviewHeaders(ALL_COLUMNS);
        setPreviewRows(dataRows);
        setTotalRows(dataRows.length);
        setPage(0);
        setSearchQuery('');
        setFile(fileToUpload);
        setIsExpanded(true);
        setActiveStep(2);
        return;
      }

      setIsValidating(true);
      try {
        const base64 = await toBase64(fileToUpload);
        const result = await validateEmployeeDirectory(agentId, base64);
        const dataRows = result.message.rows.map(row =>
          ALL_COLUMNS.map(
            col =>
              (row as unknown as Record<string, string>)[toFieldKey(col)] ?? ''
          )
        );
        setPreviewHeaders(ALL_COLUMNS);
        setPreviewRows(dataRows);
        setTotalRows(result.message.valid_rows);
        setPage(0);
        setSearchQuery('');
        setFile(fileToUpload);
        setIsExpanded(true);
        setActiveStep(2);
      } catch (error: any) {
        setFileError(error?.message || t('Upload failed. Please try again.'));
      } finally {
        setIsValidating(false);
      }
    };
    reader.readAsArrayBuffer(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const base64 = await toBase64(file);
      if (deferUpload) {
        setValue('employee_directory_staged_file', {
          fileName: file.name,
          base64,
        });
        setActiveStep(3);
        displayMessage(
          t('File staged. It will be uploaded once your agent is created.'),
          'success'
        );
      } else {
        const result = await uploadEmployeeDirectory(agentId, base64);
        setUploadResult(result);
        setActiveStep(3);
        displayMessage(
          t('Employee directory uploaded successfully.'),
          'success'
        );
      }
    } catch (error: any) {
      const message = error?.message || t('Upload failed. Please try again.');
      setUploadError(message);
      setFile(null);
      setPreviewRows([]);
      setPreviewHeaders([]);
      setTotalRows(0);
      setPage(0);
      setActiveStep(2);
      displayMessage(message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: '1rem' }}>
              {t(
                'Download the template file below and use it to fill in your employee directory. Do not modify the column headers.'
              )}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: '1.5rem' }}
            >
              {t('Required columns:')}{' '}
              {REQUIRED_COLUMNS.map((c, i) => (
                <span key={c}>
                  <strong>{c}</strong>
                  {i < REQUIRED_COLUMNS.length - 1 ? ', ' : ''}
                </span>
              ))}
              {'. '}
              {t(
                'At least one of Phone Number, Department Phone Number, or Backup Phone Number is required per row.'
              )}
              {t('Optional: {{columns}}. Max 1500 rows.', {
                columns: OPTIONAL_COLUMNS.join(', '),
              })}
            </Typography>
            <Button variant="outlined" onClick={generateTemplate}>
              <Download sx={{ mr: '0.5rem' }} />
              {t('Download Template')}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: '1rem' }}>
              {t('Fill in the template with your employee data:')}
            </Typography>
            <Box component="ul" sx={{ pl: '1.5rem', m: 0 }}>
              {[
                t('Each row represents one employee.'),
                t(
                  'First Name and Last Name are required. Each row must also have at least one of Phone Number, Department Phone Number, or Backup Phone Number.'
                ),
                t('Rows missing required fields will be skipped.'),
                t('Maximum 1500 rows are supported.'),
                t('Save the file as .xlsx before uploading.'),
              ].map(text => (
                <Typography
                  component="li"
                  variant="body2"
                  key={text}
                  sx={{ mb: '0.5rem' }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          </Box>
        );

      case 2: {
        const query = searchQuery.trim().toLowerCase();
        const filteredRows = query
          ? previewRows.filter(row =>
              row.some(cell => cell.toLowerCase().includes(query))
            )
          : previewRows;
        const paginatedRows = filteredRows.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        );
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {file ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: '1rem' }}
                >
                  {t('{{total}} rows from', { total: totalRows })}{' '}
                  <strong>{file.name}</strong>
                </Typography>
                <TextField
                  size="small"
                  placeholder={t('Search...')}
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  sx={{ mb: '0.75rem' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: isExpanded ? 'none' : 260,
                    flex: isExpanded ? 1 : 'none',
                    overflow: 'auto',
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {previewHeaders.map(h => (
                          <TableCell
                            key={h}
                            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.length > 0 ? (
                        paginatedRows.map((row, i) => (
                          <TableRow key={i}>
                            {previewHeaders.map((_, j) => (
                              <TableCell key={j} sx={{ whiteSpace: 'nowrap' }}>
                                {row[j] ?? ''}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={previewHeaders.length}
                            align="center"
                            sx={{ color: 'text.secondary', py: '1.5rem' }}
                          >
                            {t('No matching rows.')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          count={filteredRows.length}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          rowsPerPageOptions={[25, 50, 100, 500]}
                          onPageChange={(_, newPage) => setPage(newPage)}
                          onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
                <Button
                  variant="text"
                  size="small"
                  style={{ marginTop: '0.75rem' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('Choose a different file')}
                </Button>
              </>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ mb: '1rem' }}>
                  {t(
                    'Select your completed .xlsx file to preview before uploading.'
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ marginBottom: '1rem' }}
                  disabled={isValidating}
                >
                  <CloudUpload sx={{ mr: '0.5rem' }} />
                  {t('Choose .xlsx file')}
                </Button>
                {isValidating && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      mb: '1rem',
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2">{t('Loading...')}</Typography>
                  </Box>
                )}
                {uploadError && (
                  <Alert severity="error" sx={{ mb: '1rem' }}>
                    {uploadError}
                  </Alert>
                )}
                {fileError && (
                  <Alert severity="error" sx={{ mb: '1rem' }}>
                    {fileError}
                  </Alert>
                )}
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Box>
        );
      }

      case 3:
        return (
          <Box>
            {isUploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <CircularProgress size={20} />
                <Typography variant="body2">{t('Uploading...')}</Typography>
              </Box>
            )}
            {deferUpload && !isUploading && !uploadError && (
              <Alert severity="info">
                {t(
                  'File staged. It will be uploaded once your agent is created.'
                )}
              </Alert>
            )}
            {uploadResult && (
              <Alert severity="success">
                <strong>{t('Upload complete.')}</strong>{' '}
                {t(
                  '{{inserted}} entries added, {{deleted}} previous entries replaced, {{skipped}} rows skipped.',
                  {
                    inserted: uploadResult.message?.inserted,
                    deleted: uploadResult.message?.deleted_previous,
                    skipped: uploadResult.message?.skipped_invalid,
                  }
                )}
                {(uploadResult.message?.failed ?? 0) > 0 && (
                  <>
                    {' '}
                    {t('{{failed}} rows failed to insert.', {
                      failed: uploadResult.message?.failed,
                    })}
                  </>
                )}
              </Alert>
            )}
            {uploadError && <Alert severity="error">{uploadError}</Alert>}
          </Box>
        );

      default:
        return null;
    }
  };

  const canGoNext = () => {
    if (activeStep === 2 && !file) return false;
    if (activeStep === 3) return false;
    return true;
  };

  const isLastActionStep = activeStep === 2;

  return (
    <Modal
      open={open}
      onClose={isUploading || isValidating ? undefined : handleClose}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isExpanded ? '90vw' : { xs: '90%', sm: 560, md: 680 },
          height: isExpanded ? '90vh' : 'auto',
          bgcolor: 'background.paper',
          borderRadius: '4px',
          boxShadow: 24,
          p: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t('Upload Employee Directory')}
          </Typography>
          {activeStep === 2 && file && (
            <Tooltip title={isExpanded ? t('Collapse') : t('Expand')}>
              <IconButton size="small" onClick={() => setIsExpanded(e => !e)}>
                {isExpanded ? (
                  <CloseFullscreen fontSize="small" />
                ) : (
                  <OpenInFull fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step
              key={label}
              completed={index === STEPS.length - 1 && !!uploadResult}
            >
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            minHeight: 180,
            flex: isExpanded ? 1 : 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {renderStepContent()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {activeStep < 3 && !isUploading && !isValidating && (
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              {t('Cancel')}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: '0.75rem', ml: 'auto' }}>
            {activeStep > 0 &&
              activeStep < 3 &&
              !isUploading &&
              !isValidating && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setActiveStep(s => s - 1)}
                >
                  {t('Back')}
                </Button>
              )}
            {activeStep < 2 && (
              <Button
                variant="contained"
                onClick={() => setActiveStep(s => s + 1)}
                disabled={!canGoNext()}
              >
                {t('Next')}
              </Button>
            )}
            {isLastActionStep && (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!file || isUploading}
              >
                {isUploading ? (
                  <CircularProgress size={16} sx={{ mr: '0.5rem' }} />
                ) : (
                  <CloudUpload sx={{ mr: '0.5rem' }} />
                )}
                {t('Upload')}
              </Button>
            )}
            {activeStep === 3 && (uploadResult || deferUpload) && (
              <Button variant="contained" onClick={handleClose}>
                {t('Done')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EmployeeDirectoryUploadModal;
